import React, { useEffect, useCallback, useMemo, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import Button from '../../atoms/Button/Button';
import styles from './ContributeCloseButtonTemplate.css';
import useTranslate from '../../../hooks/useTranslate';
import useFetchAction from '../../../hooks/useFetchAction';
import { releaseTask as releaseTaskAction } from '../../../store/actions/task';
import { createToast } from '../../../store/actions/toast';
import { ERROR } from '../../../constants/toastType';
import * as config from '../../../helpers/config';
import { getSiteName } from '../../../helpers/url';
import { getCookie } from '../../../helpers/cookies';
import { useSelector } from 'react-redux';
import { allTasksIdsSelector, currentTaskSelector } from '../../../store/selectors/task';
import { setTimeoutIfFuture } from '../../../helpers/timeount';
import { AlertContext } from '../../molecules/Alert/AlertContext';
import { formatDuration } from '../../../helpers/time';

// eslint-disable-next-line no-console
const log = (...args) => console.log('ContributeCloseButtonTemplate - ', ...args);

const ContributeCloseButtonTemplate = ({
  taskId,
  redirectTo = 'team',
  isView,
  projectId,
  onInactivityEnd,
}) => {
  const i18n = useTranslate();
  const storeTasksIds = useSelector(allTasksIdsSelector);
  const currentTask = useSelector(currentTaskSelector);
  const taskStartedAt = useMemo(() => new Date(), [currentTask]);
  const [redirectOnError, setRedirectOnError] = useState(false);
  const [tasksIds, setTasksIds] = useState();
  const { openAlert } = useContext(AlertContext);
  const [releaseTask, , releaseError, , releaseStatus] = useFetchAction(releaseTaskAction);
  const siteKey = useMemo(() => {
    const idSite = getCookie('IdSite');
    const sites = config.get('url');

    return Object.keys(sites).find((key) => sites[key].overrides.includes(idSite)) || redirectTo;
  }, [redirectTo]);
  const url = useMemo(
    () =>
      config
        .get(`url.${siteKey}.${isView ? 'achievements' : 'projects'}`)
        .replace('{{IdProject}}', projectId),
    [siteKey, isView, projectId],
  );
  const sitename = useMemo(() => getSiteName(url), [url]);

  const goBack = () => {
    log(`redirect to ${url}`);
    window.location.href = url.replace('{{IdProject}}', projectId);
  };

  useEffect(() => {
    const handleBackButton = () => {
      const confirmation = window.confirm('Êtes-vous sûr de vouloir revenir en arrière ?');
      if (!confirmation) {
        window.history.pushState(null, null, window.location.pathname);
      } else {
        const taskId = currentTask?.taskId;
        if (taskId) {
          releaseTask(taskId, { ignoreError: true });
        }
      }
    };

    window.history.pushState(null, null, window.location.pathname);
    window.addEventListener('popstate', handleBackButton);
    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, []);

  useEffect(() => {
    if (releaseError) {
      createToast(
        ERROR,
        i18n('ContributeCloseButtonTemplate.error', { message: releaseError.message }),
      );
      if (redirectOnError) {
        goBack();
      }
    } else if (releaseStatus === 'success') {
      //  eslint-disable-next-line
      log(`Released`);
      if (tasksIds && tasksIds.length) {
        const taskId = tasksIds[0];
        setTasksIds(tasksIds.slice(1));
        releaseTask(taskId, { ignoreError: true });
        //  eslint-disable-next-line
        log(`Release next task ${taskId}`);
      } else {
        goBack();
      }
    }
  }, [goBack, releaseError, releaseStatus, tasksIds]);

  const handleClick = useCallback(() => {
    if (storeTasksIds.length && !isView) {
      setTasksIds(storeTasksIds.slice(1));
      // Make the release task call
      releaseTask(storeTasksIds[0], { ignoreError: true });
    } else {
      // Immediatly go back
      goBack();
    }
  }, [goBack, taskId, isView]);

  // Watch task timer
  useEffect(() => {
    let warnTid = null;
    let redirectTid = null;
    const expiresAt = currentTask?.expiresAt || 0;

    if (expiresAt > Date.now()) {
      warnTid = setTimeoutIfFuture(() => {
        openAlert(
          i18n('ContributeCloseButtonTemplate.alert', {
            startedAt: formatDuration(Date.now() - taskStartedAt.getTime()),
          }),
          i18n('ContributeCloseButtonTemplate.alertLabel'),
          onInactivityEnd,
        );
      }, expiresAt - 30000 - Date.now());
      redirectTid = setTimeoutIfFuture(() => {
        setRedirectOnError(true);
        handleClick();
      }, expiresAt - Date.now());
    }

    return () => {
      clearTimeout(warnTid);
      clearTimeout(redirectTid);
    };
  }, [handleClick, currentTask?.expiresAt, onInactivityEnd]);

  return (
    <Button
      icon="arrow_left"
      className={styles.ContributeCloseButtonTemplate__back}
      onClick={handleClick}
    >
      {[i18n('ContributeCloseButtonTemplate.back'), sitename].join(' ').trim()}
    </Button>
  );
};

// v3 identifiers are numbers, v5 ones are GUID strings
const ID_TYPE = PropTypes.oneOfType([PropTypes.number, PropTypes.string]);

ContributeCloseButtonTemplate.propTypes = {
  taskId: ID_TYPE,
  redirectTo: PropTypes.string,
  isView: PropTypes.bool,
  projectId: ID_TYPE,
  onInactivityEnd: PropTypes.func,
};

export default ContributeCloseButtonTemplate;
